import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Trash2, Link2, Youtube, Facebook, Instagram, Music2, Plus } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const PLATFORMS = [['youtube', 'YouTube', Youtube], ['facebook', 'Facebook', Facebook], ['instagram', 'Instagram', Instagram], ['tiktok', 'TikTok', Music2]];
const OWNERS = [['osho', 'Osho Universal'], ['teacher', 'Teacher'], ['facilitator', 'Facilitator'], ['other', 'Other']];
const ICON = Object.fromEntries(PLATFORMS.map(([v, , I]) => [v, I]));
const err = (e) => e?.response?.data?.detail || 'Something went wrong';
const inp = 'px-3 py-2 rounded-xl border border-rose/50 bg-white text-sm';

const AddSource = ({ onAdded }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ platform: 'youtube', handle: '', label: '', owner_type: 'osho' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.handle.trim() || !f.label.trim()) return toast({ title: 'Handle and display name are required' });
    setBusy(true);
    try {
      const r = await api.post('/admin/social-sources', f);
      toast({ title: 'Source added', description: r.data.platform === 'youtube' ? (r.data.last_error || `${r.data.post_count} latest videos pulled in`) : 'Paste post links below to publish its videos' });
      setF((p) => ({ ...p, handle: '', label: '' })); onAdded();
    } catch (e2) { toast({ title: 'Failed', description: err(e2) }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[130px_1fr_1fr_140px_auto] items-end" data-testid="social-add-form">
      <select value={f.platform} onChange={(e) => set('platform', e.target.value)} className={inp} data-testid="social-platform">{PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <input value={f.handle} onChange={(e) => set('handle', e.target.value)} placeholder="@handle, page or profile URL" className={inp} data-testid="social-handle" />
      <input value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Display name (e.g. Swami Gyan)" className={inp} data-testid="social-label" />
      <select value={f.owner_type} onChange={(e) => set('owner_type', e.target.value)} className={inp} data-testid="social-owner">{OWNERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <button disabled={busy} className="pill-btn justify-center" data-testid="social-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</button>
    </form>
  );
};

const PostLink = ({ src, onDone }) => {
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/social-sources/${src.id}/post-link`, { url, caption }); toast({ title: 'Posted to community feed' }); setUrl(''); setCaption(''); onDone(); }
    catch (e2) { toast({ title: 'Failed', description: err(e2) }); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2" data-testid={`social-postlink-${src.id}`}>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`Paste a ${src.platform} video/reel link`} className={`${inp} flex-1`} data-testid={`social-postlink-url-${src.id}`} />
      <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className={`${inp} flex-1`} data-testid={`social-postlink-caption-${src.id}`} />
      <button disabled={busy} className="pill-btn-outline justify-center" data-testid={`social-postlink-btn-${src.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Post</button>
    </form>
  );
};

const SourceRow = ({ src, onChanged }) => {
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);
  const Icon = ICON[src.platform];
  const yt = src.platform === 'youtube';
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.post(`/admin/social-sources/${src.id}/sync`); toast({ title: r.data.source.last_error ? 'Sync failed' : `${r.data.added} new video(s)`, description: r.data.source.last_error || undefined }); onChanged(); }
    catch (e) { toast({ title: 'Failed', description: err(e) }); } finally { setSyncing(false); }
  };
  const toggle = async () => { try { await api.patch(`/admin/social-sources/${src.id}`, { active: !src.active }); onChanged(); } catch (e) { toast({ title: 'Failed', description: err(e) }); } };
  const remove = async () => { if (!window.confirm(`Remove ${src.label}? Existing posts stay in the feed.`)) return; try { await api.delete(`/admin/social-sources/${src.id}`); onChanged(); } catch (e) { toast({ title: 'Failed', description: err(e) }); } };
  return (
    <div className="p-4 rounded-2xl bg-white/80 border border-rose/40" data-testid={`social-source-${src.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Icon className="w-5 h-5 text-mahogany shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-burgundy font-medium truncate">{src.label} <span className="text-xs text-mahogany/60 font-normal">· {OWNERS.find(([v]) => v === src.owner_type)?.[1]}</span></div>
          <a href={src.profile_url} target="_blank" rel="noreferrer" className="text-xs text-rose-italic underline break-all">{src.profile_url}</a>
          <div className="text-xs text-mahogany/60 mt-0.5" data-testid={`social-status-${src.id}`}>
            {src.post_count || 0} posts · {yt ? (src.last_error ? <span className="text-red-700">Error: {src.last_error}</span> : src.last_synced_at ? `Auto-syncs hourly · last ${new Date(src.last_synced_at + (src.last_synced_at.endsWith('Z') ? '' : 'Z')).toLocaleString()}` : 'Not synced yet') : 'Paste links to publish (auto-pull coming later)'}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-mahogany/80"><input type="checkbox" checked={src.active} onChange={toggle} data-testid={`social-active-${src.id}`} /> Active</label>
        {yt && <button onClick={sync} disabled={syncing} className="pill-btn-outline !py-1.5 text-xs" data-testid={`social-sync-${src.id}`}>{syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now</button>}
        <button onClick={remove} className="p-2 rounded-full hover:bg-rose-soft text-mahogany/70" aria-label="Remove" data-testid={`social-delete-${src.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <PostLink src={src} onDone={onChanged} />
    </div>
  );
};

export const SocialSources = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get('/admin/social-sources').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <section className="space-y-4" data-testid="social-sources">
      <div>
        <h2 className="font-serif-display text-3xl text-burgundy">Social <span className="font-italic-display text-rose-italic">Sources</span></h2>
        <p className="mt-1 text-sm text-mahogany/75">Your handles, teachers and facilitators. <strong>YouTube</strong> channels auto-post new videos every hour. For <strong>Facebook, Instagram and TikTok</strong>, paste a video/reel link and it's published under that name.</p>
      </div>
      <AddSource onAdded={load} />
      {items === null ? <Loader2 className="w-4 h-4 animate-spin text-mahogany" />
        : items.length === 0 ? <div className="p-6 text-center text-sm text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="social-empty">No sources yet. Add your first handle above.</div>
        : <div className="space-y-3">{items.map((s) => <SourceRow key={s.id} src={s} onChanged={load} />)}</div>}
    </section>
  );
};
